import { CellWrapperProps } from "@/components/elements/Advanced/Table/types";
import { Elements } from "@/components/elements/Elements";
import { Note, Notebook } from "@/components/elements/Notes/types";
import { WeatherParameters } from "@/components/elements/Weather/types";
import { request } from "@/lib/request";
import { User } from "@/lib/types";
import { TodoType } from "@/services/todolist";
import { DBSchema, IDBPDatabase, IDBPTransaction } from "idb";

export interface Schema extends DBSchema {
  appState:
    | {
        key: `elements.visibility.${string}`;
        value: boolean | null;
      }
    | {
        key: "activeProfileId";
        value: string | null;
      }
    | {
        key: "auth.dismissed";
        value: boolean;
      }
    | {
        key: "auth.user";
        value: User | null;
      }
    | {
        key: "auth.accessToken";
        value: string | null;
      }
    | {
        key: "ai.provider";
        value: string | null;
      };
  elements: {
    key: string;
    value: {
      id: string;
      name: keyof typeof Elements;
      parentId: string | null;
      childrenIds: string[];
      profileId: string | null;
      config: string;
    };
    indexes: {
      id: string;
      profileId: string;
    };
  };
  stickyNotes: {
    key: string;
    value: {
      elementId: string;
      content: string;
    };
  };
  weatherConfig: {
    key: string;
    value: {
      elementId: string;
    } & WeatherParameters;
  };
  todos: {
    key: string;
    value: TodoType;
    indexes: {
      id: string;
    };
  };
  notes: {
    key: string;
    value: Note;
    indexes: {
      id: string;
      notebookId: string;
    };
  };
  notebooks: {
    key: string;
    value: Notebook;
    indexes: {
      id: string;
    };
  };
  requests: {
    key: string;
    value: {
      id: string;
      url: string;
      method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
      isPaginated: boolean;
      headers: {
        enabled: boolean;
        key: string;
        value: string;
      }[];
      queries: {
        enabled: boolean;
        pageField: boolean;
        perPageField: boolean;
        key: string;
        value: string;
      }[];
      auth:
        | {
            type: "none";
          }
        | {
            type: "basic";
            username: string;
            password: string;
          }
        | {
            type: "bearer";
            token: string;
            prefix: string;
          };
      body: string;
    };
    indexes: {
      id: string;
      url: string;
      method: string;
    };
  };
  tableConfigurations: {
    key: string;
    value: {
      id: string;
      requestId: string;
      arrayField: string;
      countField: string;
      title: string;
      columns: {
        enabled: boolean;
        fieldName: string;
        columnName: string;
        columnType:
          | "string"
          | "number"
          | "boolean"
          | "date"
          | "array"
          | "object"
          | "unknown";
        columnDisplay: string;
        columnDisplayProps: Record<string, unknown>;
        columnStyle: CellWrapperProps;
      }[];
    };
    indexes: {
      id: string;
      requestId: string;
    };
  };
}

export const migration = async (
  _: IDBPDatabase<Schema>,
  tx: IDBPTransaction<Schema>,
) => {
  const accessToken = await tx.objectStore("appState").get("auth.accessToken");
  if (!accessToken) {
    return;
  }

  const requests = await tx.objectStore("requests").getAll();
  const tables = await tx.objectStore("tableConfigurations").getAll();

  await request
    .put(
      "/internal/table/sync",
      {
        requests,
        tables,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    .catch(() => {});
};
